/**
 * RACIBuilderModal Component (DC-1095)
 *
 * Modal for creating a RACI matrix mapping recommendations to personas.
 *
 * Redesigned architecture:
 * - Rows = ALL Bridge AI recommendations for this journey
 * - Columns = 4 baseline personas (DO, DS, DAS, DOO)
 * - Each cell = R/A/C/I selector
 * - Template selection pre-fills suggested assignments
 * - Assigned roles shown with employee names in column headers
 * - Validates at least one R and one A per row with assignments
 *
 * @version 2.0.0
 * @date 2026-01-27
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  useRACITemplates,
  useRACITemplate,
  useEmployees,
  createRACIMatrix,
  getRACIRoleLabel,
  getRACIRoleDescription,
} from './useGovernanceActions';
import type {
  RACIBuilderModalProps,
  RACIAssignment,
  RecommendationRACIAssignment,
} from './types';
import './RACIBuilderModal.css';

type RACIRole = 'R' | 'A' | 'C' | 'I' | '';

// Default baseline personas for RACI columns
const BASELINE_PERSONAS = [
  { id: 'data_owner', label: 'Data Owner', abbrev: 'DO' },
  { id: 'data_steward', label: 'Data Steward', abbrev: 'DS' },
  { id: 'data_scientist', label: 'Data Scientist', abbrev: 'DAS' },
  { id: 'document_owner', label: 'Document Owner', abbrev: 'DOO' },
];

// Grid cell state: recommendation_id -> persona_id -> RACIRole
type RACIGrid = Record<string, Record<string, RACIRole>>;

export const RACIBuilderModal: React.FC<RACIBuilderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  journeyId,
  recommendationId,
  recommendationTitle,
  raciTemplateId,
  allRecommendations,
  assignedRoles,
}) => {
  const { templates, loading: loadingTemplates } = useRACITemplates();
  const { loading: loadingEmployees } = useEmployees();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(raciTemplateId || '');
  const { template, loading: loadingTemplate } = useRACITemplate(selectedTemplateId);

  const [grid, setGrid] = useState<RACIGrid>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  // Use dynamic mode if we have recommendations to populate rows
  const hasDynamicRows = allRecommendations && allRecommendations.length > 0;

  // Build employee name lookup from assigned roles
  const personaEmployeeMap = useMemo(() => {
    const map: Record<string, { name: string; id: string }> = {};
    if (assignedRoles) {
      for (const role of assignedRoles) {
        if (role.employee_name && role.persona_id) {
          map[role.persona_id] = {
            name: role.employee_name,
            id: role.employee_id || '',
          };
        }
      }
    }
    return map;
  }, [assignedRoles]);

  // Initialize grid when recommendations change
  useEffect(() => {
    if (hasDynamicRows && allRecommendations) {
      const newGrid: RACIGrid = {};
      for (const rec of allRecommendations) {
        newGrid[rec.id] = {};
        for (const persona of BASELINE_PERSONAS) {
          newGrid[rec.id][persona.id] = '';
        }
      }
      setGrid(newGrid);
    }
  }, [hasDynamicRows, allRecommendations]);

  // Apply template defaults when template is selected (in dynamic mode)
  useEffect(() => {
    if (template && template.raci_matrix && hasDynamicRows && allRecommendations) {
      setGrid(prevGrid => {
        const newGrid = { ...prevGrid };
        // Template raci_matrix maps persona_label -> role
        // Apply as default to all recommendation rows
        for (const rec of allRecommendations) {
          if (!newGrid[rec.id]) {
            newGrid[rec.id] = {};
          }
          for (const persona of BASELINE_PERSONAS) {
            // Check if template has a mapping for this persona
            const templateRole = template.raci_matrix[persona.id] ||
              template.raci_matrix[persona.label.toLowerCase().replace(/ /g, '_')] ||
              '';
            // Only apply template default if cell is empty
            if (!newGrid[rec.id][persona.id]) {
              newGrid[rec.id][persona.id] = templateRole as RACIRole;
            }
          }
        }
        return newGrid;
      });
    }
  }, [template, hasDynamicRows, allRecommendations]);

  // Pre-select template if provided
  useEffect(() => {
    if (raciTemplateId && !selectedTemplateId) {
      setSelectedTemplateId(raciTemplateId);
    }
  }, [raciTemplateId, selectedTemplateId]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, submitting, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  // Update a single cell in the dynamic grid
  const handleCellChange = useCallback(
    (recId: string, personaId: string, role: RACIRole) => {
      setGrid(prev => ({
        ...prev,
        [recId]: {
          ...prev[recId],
          [personaId]: role,
        },
      }));
      setValidationErrors([]);
    },
    []
  );

  // Validate the dynamic grid
  const validateGrid = useCallback((): string[] => {
    const errors: string[] = [];
    if (!allRecommendations) return errors;

    for (const rec of allRecommendations) {
      const recAssignments = grid[rec.id] || {};
      const roles = Object.values(recAssignments).filter(r => r !== '');

      if (roles.length > 0) {
        const hasR = roles.includes('R');
        const hasA = roles.includes('A');
        const aCount = roles.filter(r => r === 'A').length;

        if (!hasR) {
          errors.push(`"${rec.title.substring(0, 40)}..." needs at least one Responsible (R)`);
        }
        if (!hasA) {
          errors.push(`"${rec.title.substring(0, 40)}..." needs at least one Accountable (A)`);
        }
        if (aCount > 1) {
          errors.push(`"${rec.title.substring(0, 40)}..." has multiple Accountable (A) - only one allowed`);
        }
      }
    }

    // At least one row must have assignments
    const anyAssigned = allRecommendations.some(rec => {
      const recAssignments = grid[rec.id] || {};
      return Object.values(recAssignments).some(r => r !== '');
    });

    if (!anyAssigned) {
      errors.push('At least one recommendation must have RACI assignments');
    }

    return errors;
  }, [grid, allRecommendations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (hasDynamicRows) {
      // Dynamic mode: validate grid and submit recommendation_assignments
      const errors = validateGrid();
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      try {
        setSubmitting(true);
        setError(null);
        setValidationErrors([]);

        // Build recommendation assignments
        const recommendationAssignments: RecommendationRACIAssignment[] = [];
        const flatAssignments: RACIAssignment[] = [];

        for (const rec of allRecommendations!) {
          const recAssignments = grid[rec.id] || {};
          const hasAnyRole = Object.values(recAssignments).some(r => r !== '');

          if (hasAnyRole) {
            recommendationAssignments.push({
              recommendation_id: rec.id,
              recommendation_title: rec.title,
              raci: recAssignments as Record<string, 'R' | 'A' | 'C' | 'I' | ''>,
            });

            // Also build flat assignments for backward compatibility
            for (const [personaId, role] of Object.entries(recAssignments)) {
              if (role) {
                const employee = personaEmployeeMap[personaId];
                flatAssignments.push({
                  role: role as 'R' | 'A' | 'C' | 'I',
                  persona_label: personaId,
                  employee_id: employee?.id || undefined,
                  employee_name: employee?.name || undefined,
                });
              }
            }
          }
        }

        const action = await createRACIMatrix({
          journey_id: journeyId,
          recommendation_id: recommendationId,
          recommendation_title: recommendationTitle,
          raci_template_id: selectedTemplateId || 'dynamic',
          template_name: template?.name || 'Dynamic RACI',
          assignments: flatAssignments,
          recommendation_assignments: recommendationAssignments,
        });

        setSuccess(true);
        setTimeout(() => {
          onSuccess(action);
          onClose();
        }, 1500);
      } catch (err: unknown) {
        console.error('[RACIBuilderModal] Submit error:', err);
        setError(err instanceof Error ? err.message : 'Failed to create RACI matrix');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Legacy mode (no recommendations passed): use template-based flow
      // This preserves backward compatibility
      if (!selectedTemplateId) {
        setError('Please select a RACI template');
        return;
      }

      try {
        setSubmitting(true);
        setError(null);

        const action = await createRACIMatrix({
          journey_id: journeyId,
          recommendation_id: recommendationId,
          recommendation_title: recommendationTitle,
          raci_template_id: selectedTemplateId,
          template_name: template?.name,
          assignments: [],
        });

        setSuccess(true);
        setTimeout(() => {
          onSuccess(action);
          onClose();
        }, 1500);
      } catch (err: unknown) {
        console.error('[RACIBuilderModal] Submit error:', err);
        setError(err instanceof Error ? err.message : 'Failed to create RACI matrix');
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Truncate long recommendation titles for table cells
  const truncateTitle = (title: string, maxLen: number = 50): string => {
    if (title.length <= maxLen) return title;
    return title.substring(0, maxLen) + '...';
  };

  if (!isOpen) return null;

  const loading = loadingTemplates || loadingEmployees || loadingTemplate;

  return createPortal(
    <div
      className="modal-overlay raci-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="raci-builder-title"
    >
      <div className={`modal-container raci-builder-modal${hasDynamicRows ? ' raci-wide' : ''}`}>
        <div className="modal-header">
          <h2 id="raci-builder-title" className="modal-title">
            Create RACI Matrix
          </h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Recommendation Context */}
            {recommendationTitle && (
              <div className="recommendation-context">
                <span className="context-label">From recommendation:</span>
                <span className="context-value">{recommendationTitle}</span>
              </div>
            )}

            {/* Template Selector (optional in dynamic mode, required in legacy mode) */}
            <div className="form-group">
              <label htmlFor="template-select" className="form-label">
                {hasDynamicRows ? 'Apply Template Defaults (optional)' : 'Select RACI Template'}
              </label>
              <select
                id="template-select"
                className="form-select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={loading || submitting}
              >
                <option value="">{hasDynamicRows ? '-- No template (manual) --' : '-- Select a template --'}</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name} ({t.activity_type})
                  </option>
                ))}
              </select>
              {!loadingTemplates && templates.length === 0 && (
                <span className="form-hint">
                  No templates found. Run the governance template seeder or contact your administrator.
                </span>
              )}
            </div>

            {/* Template Info */}
            {template && (
              <div className="template-info">
                <h3 className="template-name">{template.name}</h3>
                {template.description && (
                  <p className="template-description">{template.description}</p>
                )}
              </div>
            )}

            {/* RACI Legend */}
            <div className="raci-legend">
              <h4 className="legend-title">RACI Legend</h4>
              <div className="legend-items">
                {(['R', 'A', 'C', 'I'] as const).map((role) => (
                  <div key={role} className={`legend-item role-${role}`}>
                    <span className="legend-letter">{role}</span>
                    <span className="legend-label">{getRACIRoleLabel(role)}</span>
                    <span className="legend-desc">{getRACIRoleDescription(role)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic RACI Matrix (Rows = Recommendations, Columns = Personas) */}
            {hasDynamicRows && allRecommendations && allRecommendations.length > 0 && (
              <div className="raci-matrix raci-matrix-dynamic">
                <div className="matrix-header-dynamic">
                  <span className="col-recommendation-header">Recommendation</span>
                  {BASELINE_PERSONAS.map((persona) => {
                    const employee = personaEmployeeMap[persona.id];
                    return (
                      <span key={persona.id} className="col-persona-header">
                        <span className="persona-abbrev">{persona.abbrev}</span>
                        {employee && (
                          <span className="persona-employee" title={employee.name}>
                            {employee.name.split(' ')[0]}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
                <div className="matrix-body-dynamic">
                  {allRecommendations.map((rec) => (
                    <div key={rec.id} className="matrix-row-dynamic">
                      <span className="col-recommendation" title={rec.title}>
                        <span className={`rec-priority priority-${rec.priority}`}>
                          {rec.priority === 'critical' ? 'C' : rec.priority === 'warning' ? 'W' : 'O'}
                        </span>
                        {truncateTitle(rec.title)}
                      </span>
                      {BASELINE_PERSONAS.map((persona) => (
                        <span key={persona.id} className="col-raci-cell">
                          <select
                            className={`raci-select${grid[rec.id]?.[persona.id] ? ` has-value role-${grid[rec.id][persona.id]}` : ''}`}
                            value={grid[rec.id]?.[persona.id] || ''}
                            onChange={(e) =>
                              handleCellChange(rec.id, persona.id, e.target.value as RACIRole)
                            }
                            disabled={submitting}
                            aria-label={`${persona.label} role for ${rec.title}`}
                          >
                            <option value="">-</option>
                            <option value="R">R</option>
                            <option value="A">A</option>
                            <option value="C">C</option>
                            <option value="I">I</option>
                          </select>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback: No recommendations available */}
            {!hasDynamicRows && !template && selectedTemplateId === '' && !loadingTemplates && (
              <div className="empty-state">
                {templates.length === 0
                  ? 'No RACI templates found. Run the governance template seeder or contact your administrator.'
                  : 'Select a RACI template to begin'}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="loading-state">Loading...</div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="validation-errors" role="alert">
                <ul>
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="success-message" role="status">
                RACI matrix created successfully!
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(!hasDynamicRows && !selectedTemplateId) || submitting || loading || success}
            >
              {submitting ? 'Creating...' : 'Create RACI Matrix'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default RACIBuilderModal;
